// ClientDialog.js - Dialog für Hinzufügen/Bearbeiten eines Kunden
import React, { useState } from 'react';
import Dialog from '../common/Dialog';
import { ClientFormStep1, ClientFormStep2, ClientFormStep3, ClientFormStep4 } from './ClientForm';
import Button from '../common/Button';
import ErrorAlert from '../common/ErrorAlert';

/**
 * Multi-Step-Dialog zum Hinzufügen oder Bearbeiten eines Kunden
 *
 * @param {Object} props - Komponenteneigenschaften
 * @param {boolean} props.isOpen - Gibt an, ob der Dialog geöffnet ist
 * @param {Function} props.onClose - Callback-Funktion beim Schließen des Dialogs
 * @param {Function} props.onSave - Callback-Funktion beim Speichern des Kunden
 * @param {Object} props.clientData - Daten des Kunden
 * @param {Function} props.setClientData - Funktion zum Aktualisieren der Kundendaten
 * @param {string} props.error - Fehlermeldung (optional)
 * @param {boolean} props.isEdit - Gibt an, ob ein Kunde bearbeitet wird (sonst: neu hinzufügen)
 */
const ClientDialog = ({ 
  isOpen, 
  onClose, 
  onSave, 
  clientData, 
  setClientData, 
  error,
  isEdit = false 
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  
  // Handler für verschiedene Input-Typen
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setClientData({
      ...clientData,
      [name]: value
    });
  };

  const handleContactPersonChange = (e, index) => {
    const { name, value } = e.target;
    const updatedContactPersons = [...clientData.contactPersons];
    updatedContactPersons[index][name] = value;
    setClientData({ ...clientData, contactPersons: updatedContactPersons });
  };

  const handleAddressChange = (e, type, index) => {
    const { name, value } = e.target;
    const updatedAddresses = { ...clientData.addresses };
    updatedAddresses[type][index][name] = value;
    setClientData({ ...clientData, addresses: updatedAddresses });
  };

  const handleEmailChange = (e, type, index) => {
    const { value } = e.target;
    const updatedEmails = { ...clientData.emailAddresses };
    updatedEmails[type][index] = value;
    setClientData({ ...clientData, emailAddresses: updatedEmails });
  };

  const handlePhoneChange = (e, type, index) => {
    const { value } = e.target;
    const updatedPhones = { ...clientData.phoneNumbers };
    updatedPhones[type][index] = value;
    setClientData({ ...clientData, phoneNumbers: updatedPhones });
  };

  // Navigation zwischen Schritten
  const handleNextStep = () => setCurrentStep(currentStep + 1);
  const handlePreviousStep = () => setCurrentStep(currentStep - 1);

  // Dialog-Titel basierend auf Edit-Modus
  const dialogTitle = isEdit ? 'Kunden bearbeiten' : 'Neuen Kunden hinzufügen';

  // Schritt-Indikator
  const StepIndicator = () => (
    <div className="flex justify-between mb-4">
      {[1, 2, 3, 4].map(step => (
        <span 
          key={step}
          className={`px-3 py-1 rounded-full ${currentStep === step ? 'bg-[#0000FF] text-white' : 'bg-gray-200'}`}
        >
          {step}
        </span>
      ))}
    </div>
  );

  // Aktionsbuttons basierend auf aktuellem Schritt
  const renderActionButtons = () => {
    if (currentStep === 1) {
      return (
        <>
          <Button variant="text" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleNextStep}>
            Weiter
          </Button>
        </>
      );
    } else if (currentStep < 4) {
      return (
        <>
          <Button variant="text" onClick={handlePreviousStep}>
            Zurück
          </Button>
          <Button variant="primary" onClick={handleNextStep}>
            Weiter
          </Button>
        </>
      );
    } else {
      return (
        <>
          <Button variant="text" onClick={handlePreviousStep}>
            Zurück
          </Button>
          <Button variant="primary" onClick={onSave}>
            Speichern
          </Button>
        </>
      );
    }
  };

  // Renderlogik für den aktuellen Schritt
  const renderCurrentStep = () => {
    switch(currentStep) {
      case 1:
        return (
          <ClientFormStep1 
            clientData={clientData} 
            onChange={handleInputChange} 
          />
        );
      case 2:
        return (
          <ClientFormStep2 
            contactPersons={clientData.contactPersons} 
            onContactPersonChange={handleContactPersonChange}
          />
        );
      case 3:
        return (
          <ClientFormStep3 
            addresses={clientData.addresses} 
            onAddressChange={handleAddressChange}
          />
        );
      case 4:
        return (
          <ClientFormStep4 
            emailAddresses={clientData.emailAddresses}
            phoneNumbers={clientData.phoneNumbers}
            note={clientData.note}
            onEmailChange={handleEmailChange}
            onPhoneChange={handlePhoneChange}
            onNoteChange={handleInputChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={dialogTitle}
      size="lg"
      actions={renderActionButtons()}
    >
      <StepIndicator />
      {error && <ErrorAlert message={error} className="mb-4" />}
      {renderCurrentStep()}
    </Dialog>
  );
};

export default ClientDialog;
